import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { PaymentWallets } from '@root/features/wallet/payment-wallets/ui/payment-wallets'

import { reatomWithdrawReferral } from '../../atoms'

import { ReferralWithdrawAmount } from './referral-withdraw-amount'
import { ReferralWithdrawProcessing } from './referral-withdraw-processing'

import type { DeviceType } from '@blade/ui-kit'
import type { ClassNameProps } from '@root/shared/react'

// TODO: User should not be able to change wallet

type Props = {
  device: DeviceType
  onClose: () => void
} & ClassNameProps

export const ReferralWithdraw: React.FC<Props> = ({
  device,
  onClose,
  className,
}) => {
  const { t } = useTranslation()

  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomWithdrawReferral(),
    [],
  )
  const [withdrawState] = useAtom(model.withdrawRequestStateAtom)
  const [withdrawWallet, setWithdrawWallet] = useAtom(model.withdrawWallet)
  const [withdrawAmountAvailable] = useAtom(model.withdrawAmountAvailable)
  const [withdrawAmount] = useAtom(model.withdrawAmount)
  const withdraw = useAction(model.withdrawAction)

  const handleWithdraw = () => withdraw()
  const handleClose = () => onClose()

  if (AsyncStateN.isSuccess(withdrawState)) {
    return <ReferralWithdrawProcessing />
  }

  return (
    <Root className={className}>
      <Header $device={device}>
        <BalanceLabel>
          {t('referral.withdraw.your-referral-balance')}
        </BalanceLabel>
        <Balance>
          <Numeric
            preset="currency-amount-short"
            currency={nativeCurrency.ticker}
            value={withdrawAmountAvailable}
          />
        </Balance>
        <SendTo>{t('referral.withdraw.send-to')}</SendTo>
        <PaymentWallets
          label={t('referral.withdraw.select_wallet')}
          device={device}
          paymentSource={withdrawWallet}
          setPaymentSource={setWithdrawWallet}
        />
      </Header>
      <ReferralWithdrawAmount model={model} />
      <Actions $device={device}>
        <Button
          disabled={AsyncStateN.isLoading(withdrawState)}
          variant="secondary"
          size={device === 'desktop' ? 's' : 'm'}
          radius="rounded"
          onClick={handleClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          disabled={
            !withdrawWallet ||
            AsyncStateN.isLoading(withdrawState) ||
            withdrawAmount.isZero() ||
            withdrawAmountAvailable.isLessThan(withdrawAmount)
          }
          busy={AsyncStateN.isLoading(withdrawState)}
          variant="solid"
          color="primary"
          radius="rounded"
          size={device === 'desktop' ? 's' : 'm'}
          onClick={handleWithdraw}
        >
          {t('common.next')}
        </Button>
      </Actions>
    </Root>
  )
}

const Root = styled.div`
  background: var(--appearance-1);
  display: grid;

  border-radius: 12px;
`

const Header = styled.div<{ $device: DeviceType }>`
  display: grid;
  background: var(--backgroundA-3);
  border-radius: 12px;
  gap: 12px;

  padding: ${({ $device }) =>
    $device === 'desktop' ? '20px 30px 20px' : '12px 16px 12px'};
`

const Actions = styled.div<{ $device: DeviceType }>`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 9px;
  align-items: center;
  padding: ${({ $device }) =>
    $device === 'desktop' ? '34px 30px 0' : '12px 16px 0'};
`

const BalanceLabel = styled.div`
  color: rgba(255, 255, 255, 0.25);

  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  text-transform: uppercase;
`

const Balance = styled.div`
  color: var(--text-12);

  font-size: 18px;
  font-weight: 700;
  text-align: center;
`

const SendTo = styled.div`
  color: var(--text-8);

  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;

  display: grid;
  align-content: center;
  align-items: center;

  grid-template-columns: 1fr auto 1fr;
  gap: 5px;

  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: var(--background-3);
  }

  &:before {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: var(--background-3);
  }
`
